import React, { useCallback, useRef } from 'react';

import type { TagEntity } from '@schibsted/frapi-schema';
import type { ByfunnFrapiSearchResponse } from '../../../types/frapi-types.js';

import { usePulseContext } from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import { useOutsideClick } from '../../../../core/react/hooks/index.js';
import { frontendConfig } from '../../../../core/system/config/frontend.server.js';
import { sectionMappings } from '../../layouts/front/sectionHelpers.js';

export interface SearchBarInputProps {
  searchResult: ByfunnFrapiSearchResponse | null;
  searchTerm: string;
  presetTerm?: string;
  section: string;
  activeFilters: TagEntity[];
  setSearchTerm: (text: string) => void;
  setShowPreview: (showPreview: boolean) => void;
}

export const SearchBarInput: React.FC<SearchBarInputProps> = ({
  searchResult,
  searchTerm,
  section,
  setSearchTerm,
  setShowPreview,
  presetTerm,
  activeFilters,
}) => {
  const handleClickOutside = () => {
    setShowPreview(false);
  };

  const containerRef = useOutsideClick(handleClickOutside);

  const { trackClickUIElement } = usePulseContext();

  const isClickTracked = useRef<boolean>(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setShowPreview(true);
    },
    [setSearchTerm, setShowPreview],
  );

  const handleClick = useCallback(() => {
    if (isClickTracked.current) return;

    trackClickUIElement({
      object: {
        id: 'search-bar',
        name: 'focus search bar',
        elementType: 'Input',
      },
    });

    isClickTracked.current = true;
  }, [trackClickUIElement]);

  const handleClear = useCallback(() => {
    setSearchTerm('');
  }, [setSearchTerm]);

  const hasResults = searchResult?.articles && searchTerm;
  const noTermLink =
    activeFilters.length > 0
      ? `/samling/${section}/${activeFilters.map((f) => f.id).join(',')}`
      : '/sok';

  return (
    <div className="search-input" ref={containerRef}>
      {presetTerm && (
        <div className="active-term-container">
          Ditt søk:
          <a className="active-term" href={noTermLink}>
            {`"${presetTerm}"`}
          </a>
        </div>
      )}
      <div className="search-input-wrapper">
        <input
          className={hasResults ? 'active' : 'inactive'}
          type="text"
          name="name"
          onClick={handleClick}
          onChange={handleChange}
          value={presetTerm === searchTerm ? '' : searchTerm}
          placeholder={sectionMappings[section]?.placeholder || 'Søk i Vink'}
          autoComplete="off"
        />
      </div>
      {searchTerm && (
        <button className="clear-search" onClick={handleClear}>
          <img
            src={`${frontendConfig.cdnUrlInternal}/vink/images/icons/cross_bold.svg`}
            alt="nullstill"
          />
        </button>
      )}
    </div>
  );
};
